import React from 'react';
import { Table, Button, UncontrolledTooltip } from 'reactstrap';
import moment from 'moment';
import FrequencyDisplay from './FrequencyDisplay';
import NextRunCountdown from './NextRunCountdown';
import AccuracyIndicator from './AccuracyIndicator';
import ExecutionHistoryDots from './ExecutionHistoryDots';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';

/**
 * Service List Table Component
 * Displays all monitored services in a table format
 *
 * Props:
 * - services: Array of ServiceConfigDto objects
 * - onServiceClick: Function to call when Details button is clicked
 *
 * Columns:
 * 1. Service Name (with scheduling type badge)
 * 2. Status (Running/Stopped with color)
 * 3. Frequency (with scheduling type)
 * 4. Interval (Timer: formatted milliseconds, Cron: description)
 * 5. Next Run (countdown with accuracy indicator)
 * 6. Last 5 Runs (colored dots)
 * 7. Actions (Details button)
 */
const ServiceListTable = ({ services, onServiceClick }) => {
  // Check if user has permission to view service details
  const canViewDetails = AuthService.canView(PermissionFeature.MonitoringDashboardViewDetails);

  // Helper function to format milliseconds to readable interval
  const formatInterval = (milliseconds) => {
    if (!milliseconds) return 'N/A';

    const seconds = milliseconds / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;

    if (days >= 1) {
      const dayCount = Math.floor(days);
      return `${dayCount} ${dayCount === 1 ? 'day' : 'days'}`;
    } else if (hours >= 1) {
      const hourCount = Math.floor(hours);
      return `${hourCount} ${hourCount === 1 ? 'hour' : 'hours'}`;
    } else if (minutes >= 1) {
      const minuteCount = Math.floor(minutes);
      return `${minuteCount} ${minuteCount === 1 ? 'minute' : 'minutes'}`;
    } else {
      const secondCount = Math.floor(seconds);
      return `${secondCount} ${secondCount === 1 ? 'second' : 'seconds'}`;
    }
  };

  // Helper function to get interval display based on scheduling type
  const getIntervalDisplay = (service) => {
    if (service.schedulingType === 'Cron') {
      return service.cronDescription || service.cronExpression || 'N/A';
    } else {
      return formatInterval(service.timerIntervalMilliseconds);
    }
  };

  // Helper function to determine success rate badge color
  const getSuccessRateBadgeClass = (rate) => {
    if (rate >= 95) return 'badge badge-success';
    if (rate >= 80) return 'badge badge-warning';
    return 'badge badge-danger';
  };

  // Helper function to get status badge
  const getStatusBadge = (service) => {
    if (service.isRunning === 0) {
      return <span className="badge badge-secondary">Stopped</span>;
    }

    if (service.lastExecutionStatus === 'Failed' || service.lastExecutionStatus === 'Error') {
      return <span className="badge badge-danger">Running (Error)</span>;
    }

    return <span className="badge badge-success">Running</span>;
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white border-bottom">
        <h5 className="mb-0">
          <i className="fa fa-list mr-2"></i>
          Monitored Services
        </h5>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 service-list-table">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '18%' }}>Service Name</th>
                <th style={{ width: '10%' }}>Status</th>
                <th style={{ width: '12%' }}>Frequency</th>
                <th style={{ width: '13%' }}>Interval</th>
                <th style={{ width: '13%' }}>Next Run</th>
                <th style={{ width: '18%' }}>Last 5 Runs</th>
                {/* <th style={{ width: '10%' }}>Success Rate</th> */}
                {canViewDetails && <th style={{ width: '16%' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr>
                  <td colSpan={canViewDetails ? "8" : "7"} className="text-center text-muted py-4">
                    <i className="fa fa-info-circle mr-2"></i>
                    No services found
                  </td>
                </tr>
              ) : (
                services.map((service) => (
                  <tr key={service.serviceId}>
                    {/* Service Name */}
                    <td>
                      <div className="service-name-container">
                        <div>
                          <strong className="d-block">{service.displayName}</strong>
                          <small className="text-muted">
                            <span className="badge badge-info badge-sm d-none">
                              {service.schedulingType}
                            </span>
                          </small>
                        </div>
                        {service.description && (
                          <>
                            <i
                              id={`tooltip-${service.serviceId}`}
                              className="fa fa-info-circle service-info-icon"
                            />
                            <UncontrolledTooltip
                              placement="top"
                              target={`tooltip-${service.serviceId}`}
                            >
                              <div>
                                <strong>Description:</strong>
                                <br />
                                {service.description}
                              </div>
                            </UncontrolledTooltip>
                          </>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td>
                      {getStatusBadge(service)}
                    </td>

                    {/* Frequency */}
                    <td>
                      <FrequencyDisplay
                        frequency={service.executionFrequency}
                        schedulingType={service.schedulingType}
                      />
                    </td>

                    {/* Interval */}
                    <td>
                      <span className="text-muted">
                        {getIntervalDisplay(service)}
                      </span>
                       
                    </td>

                    {/* Next Run */}
                    <td style={{ paddingRight: '1rem' }}>
                      <div className="d-flex align-items-start">
                        <div className="flex-grow-1">
                          <NextRunCountdown
                            nextRun={service.nextScheduledRun}
                            accuracy={service.nextRunAccuracy}
                            isRunning={service.isRunning}
                            isExecuting={service.isExecuting}
                            executionStartTime={service.executionStartTime}
                            canViewDuration={canViewDetails}
                            lastScheduledBeforeStop={service.lastScheduledBeforeStop}
                          />
                         </div>
                         {/* Only show accuracy indicator for running services */}
                         {service.isRunning === 1 && (
                             <div className="mr-4">
                               <AccuracyIndicator accuracy={service.nextRunAccuracy} />
                             </div>
                         )}
                        {/* Show info icon for stopped services with historical schedule */}
                        {service.isRunning === 0 && service.lastScheduledBeforeStop && (
                          <>
                            <i
                              id={`nextrun-info-${service.serviceId}`}
                              className="fa fa-info-circle text-info ml-2"
                              style={{ fontSize: '0.875rem', marginTop: '2px', cursor: 'help' }}
                            />
                            <UncontrolledTooltip
                              placement="top"
                              target={`nextrun-info-${service.serviceId}`}
                            >
                              <div>
                                <strong>Historical Schedule:</strong>
                                <br />
                                Was scheduled for{' '}
                                {moment.utc(service.lastScheduledBeforeStop).local().format('MMM DD, YYYY h:mm A')}
                                <br />
                                <small className="text-muted">
                                  (Service stopped before this execution)
                                </small>
                              </div>
                            </UncontrolledTooltip>
                          </>
                        )}
                       
                      </div>
                    </td>

                    {/* Last 5 Runs */}
                    <td>
                      <ExecutionHistoryDots serviceName={service.serviceName} />
                    </td>

                    {/* Success Rate */}
                    {/* <td>
                      {service.totalExecutions > 0 ? (
                        <span className={getSuccessRateBadgeClass(service.successRate)}>
                          {service.successRate.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td> */}

                    {/* Actions */}
                    {canViewDetails && (
                      <td>
                        <Button
                          size="sm"
                          color="primary"
                          onClick={() => onServiceClick(service.serviceName)}
                        >
                          <i className="fa fa-info-circle mr-1"></i>
                          Details
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default ServiceListTable;
